/**
 * The login context, read from the access token rather than from localStorage.
 *
 * WHY THIS EXISTS
 * ---------------
 * Roughly ten call sites read `localStorage.getItem("selectedInstituteId")` and
 * fall back to `0` or `""`. Nothing in the app has ever WRITTEN that key — so
 * those reads have always returned null and every one of them silently scoped
 * its request to institute 0. This module replaces that dead read with the
 * institute actually chosen at sign-in.
 *
 * Decoding here is NOT a security check — a JWT payload is public and
 * `jwtDecode` does not verify the signature. The security lives on the server:
 * TenantScopeMiddleware compares whatever the client sends against the same
 * claim, verified against SECRET_KEY. This module exists so the client sends the
 * RIGHT value, not so it can be trusted for having done so.
 *
 * Returns null for a token minted before context selection shipped. Callers must
 * keep their existing fallback for that case — refresh tokens live 15 days, so
 * such tokens stay in circulation for a fortnight after release.
 */

import { jwtDecode } from 'jwt-decode';

export type TenantRole =
  | 'platform'
  | 'owner'
  | 'admin'
  | 'faculty'
  | 'staff'
  | 'student'
  | 'parent';

export interface TenantClaims {
  membershipUid: string;
  /** Null for the platform context, which sits outside any institute. */
  instituteId: string | null;
  role: TenantRole;
  /** Present only for a parent context — the child this session views. */
  childEmail: string | null;
}

interface RawClaims {
  mid?: string;
  iid?: number | string;
  role?: TenantRole;
  child?: string | null;
}

const readAccessToken = (): string | null => {
  try {
    return (
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('access_token')
    );
  } catch {
    // Storage can throw in private mode or with site data blocked.
    return null;
  }
};

/** Synchronous decode, for callers that already hold the token. */
export const readTenantClaims = (
  accessToken?: string | null
): TenantClaims | null => {
  if (!accessToken) return null;

  try {
    const payload = jwtDecode<RawClaims>(accessToken);

    // `mid` alone decides whether a context was chosen. `iid` is legitimately
    // null for the platform context, so testing it here would report a platform
    // session as "no claim" — and resolveInstituteId would then fall back to a
    // stale stored id, possibly from a DIFFERENT institute.
    if (!payload?.mid) {
      return null; // pre-context token
    }
    return {
      membershipUid: String(payload.mid),
      instituteId:
        payload.iid === undefined || payload.iid === null
          ? null
          : String(payload.iid),
      role: (payload.role ?? 'student') as TenantRole,
      childEmail: payload.child ?? null,
    };
  } catch {
    // Malformed token — the request layer surfaces the real auth error.
    return null;
  }
};

/**
 * Marks that this session has held a context claim at least once.
 *
 * A token with no claim means two completely different things — "minted before
 * context selection shipped" and "the context was withdrawn" — and the legacy
 * fallback in resolveInstituteId is correct for the first and dangerous for the
 * second. This is the only thing that tells them apart.
 *
 * Cleared on sign-out (AuthContext.logout), so a later claimless sign-in on the
 * same browser is not misread as a revocation. That case is not hypothetical:
 * production has MULTI_TENANT_CONTEXT_ENABLED off, so every sign-in there is
 * claimless today.
 */
export const CONTEXT_SEEN_KEY = 'tenant_context_seen';

export const getTenantClaims = (): TenantClaims | null => {
  const claims = readTenantClaims(readAccessToken());
  if (claims) {
    try {
      localStorage.setItem(CONTEXT_SEEN_KEY, '1');
    } catch {
      // Private mode, or site data blocked. The fallback below stays as it was.
    }
  }
  return claims;
};

/**
 * Did this session hold a context that has since been withdrawn?
 *
 * When an institute removes someone, `jwt_utils.copy_context_claims` DROPS the
 * claims from every derived token rather than refusing the refresh — the
 * session survives, deliberately, and simply stops being scoped
 * ("CONTEXT_REVOKED" in the server log). Without this the client could not tell
 * that from a legacy token, so it fell through to the stored institute id — the
 * institute the user was just removed from — and kept scoping requests to it.
 */
export const isContextRevoked = (): boolean => {
  if (getTenantClaims()) return false;
  try {
    return localStorage.getItem(CONTEXT_SEEN_KEY) !== null;
  } catch {
    // Storage unreadable — treat as a legacy token rather than stripping scope
    // from a working session over a storage failure.
    return false;
  }
};

/**
 * The institute for the current session, or null when the token predates
 * context selection.
 */
export const getTenantInstituteId = (): string | null =>
  getTenantClaims()?.instituteId ?? null;

/**
 * Drop-in replacement for `localStorage.getItem("selectedInstituteId") || 0`.
 *
 * Prefers the signed claim, then the legacy keys (for pre-context tokens), then
 * the caller's own default — so a call site can adopt it without changing the
 * value it ends up sending for sessions that have no claim yet.
 */
export const resolveInstituteId = (
  fallback: string | number | null = null
): string | number | null => {
  const claims = getTenantClaims();

  // A claim is authoritative even when it names no institute (platform
  // context): returning its null rather than falling through is what stops a
  // platform session picking up a previous institute session's stored id.
  if (claims) return claims.instituteId ?? fallback;

  // Revoked, not legacy. The stored keys below hold the institute this session
  // was just removed from, so they are skipped and the server decides what an
  // unscoped session may see.
  if (isContextRevoked()) return fallback;

  try {
    const legacy =
      localStorage.getItem('selectedInstituteId') ||
      localStorage.getItem('institute_id');
    if (legacy && legacy.trim() && legacy.trim() !== '0') return legacy;

    // Deliberately NOT read here: the stored user blob's institution_id.
    // Only ONE call site (CoursePageSimplifiedRefactored) ever consulted it.
    // Reading it for everyone would change what the other sites send whenever
    // that field happens to be populated — they used to send 0, and would start
    // sending a real institute. Correct-looking, but a behaviour change in code
    // paths nobody asked us to touch. That call site keeps its own fallback
    // inline instead, so every site stays byte-identical for a pre-context
    // session.
  } catch {
    // ignore — malformed JSON, or storage blocked; fall through to the default
  }

  return fallback;
};
